/**
 * Shop by Color Section
 * Handles color swatch filtering and product carousel functionality
 */
class ShopByColor {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = this.section.dataset.sectionId;
    this.productsLimit = parseInt(this.section.dataset.productsLimit, 10) || 10;

    // Cache DOM elements
    this.swatches = this.section.querySelectorAll('.shop-by-color__swatch');
    this.carousel = this.section.querySelector('.js-shop-color-carousel');
    this.prevBtn = this.section.querySelector('.js-shop-color-prev');
    this.nextBtn = this.section.querySelector('.js-shop-color-next');
    this.currentColorName = this.section.querySelector('.js-current-color-name');
    this.loadingEl = this.section.querySelector('.js-shop-color-loading');
    this.emptyEl = this.section.querySelector('.js-shop-color-empty');
    this.featuredCard = this.section.querySelector('.js-shop-color-featured');

    this.init();
  }

  init() {
    this.bindEvents();
    this.loadInitialProducts();
  }

  bindEvents() {
    // Swatch click handlers
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', () => this.handleSwatchClick(swatch));
    });

    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollCarousel('prev'));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollCarousel('next'));
    }

    // Carousel scroll handler
    if (this.carousel) {
      this.carousel.addEventListener('scroll', () => this.updateNavButtons());
    }
  }

  handleSwatchClick(swatch) {
    // Update active state
    this.swatches.forEach(s => s.classList.remove('active'));
    swatch.classList.add('active');

    // Get color data
    const colorLabel = swatch.dataset.colorLabel;
    const colorValue = swatch.dataset.colorValue;
    const paramName = swatch.dataset.paramName;

    // Update current color display
    if (this.currentColorName) {
      this.currentColorName.textContent = colorLabel;
    }

    // Fetch products for selected color
    this.fetchFilteredProducts(paramName, colorValue);
  }

  updateNavButtons() {
    if (!this.carousel || !this.prevBtn || !this.nextBtn) return;

    const cards = this.carousel.querySelectorAll('.shop-by-color__card');
    const cardCount = cards.length;

    // Hide nav buttons if there are fewer than 3 cards
    if (cardCount < 3) {
      this.prevBtn.classList.add('hidden');
      this.nextBtn.classList.add('hidden');
      return;
    }

    const scrollLeft = this.carousel.scrollLeft;
    const scrollWidth = this.carousel.scrollWidth;
    const clientWidth = this.carousel.clientWidth;

    // Show/hide prev button based on scroll position
    if (scrollLeft <= 10) {
      this.prevBtn.classList.add('hidden');
    } else {
      this.prevBtn.classList.remove('hidden');
    }

    // Show/hide next button based on scroll position
    if (scrollLeft + clientWidth >= scrollWidth - 10) {
      this.nextBtn.classList.add('hidden');
    } else {
      this.nextBtn.classList.remove('hidden');
    }
  }

  scrollCarousel(direction) {
    if (!this.carousel) return;

    const cards = this.carousel.querySelectorAll('.shop-by-color__card');
    if (cards.length === 0) return;

    const cardWidth = cards[0].offsetWidth + 15; // Include gap
    const scrollAmount = direction === 'next' ? cardWidth : -cardWidth;

    this.carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }

  async fetchFilteredProducts(paramName, colorValue) {
    // Show loading state
    this.loadingEl.classList.add('active');
    this.emptyEl.classList.remove('active');
    this.carousel.innerHTML = '';

    // Hide featured card during loading
    if (this.featuredCard) {
      this.featuredCard.style.display = 'none';
    }

    try {
      // Build filter URL using /collections/all for global filtering
      const filterUrl = `/collections/all?${paramName}=${encodeURIComponent(colorValue)}&view=shop-by-color-products`;
      const response = await fetch(filterUrl);
      const html = await response.text();

      // Parse response HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const products = doc.querySelectorAll('.shop-by-color-product-item');

      // Hide loading state
      this.loadingEl.classList.remove('active');

      if (products.length === 0) {
        this.emptyEl.classList.add('active');
        if (this.featuredCard) {
          this.featuredCard.style.display = 'none';
        }
        this.updateNavButtons();
        return;
      }

      // Process products - first one goes to featured, rest to carousel
      let count = 0;
      let isFirstProduct = true;

      products.forEach((product) => {
        if (count >= this.productsLimit) return;

        const productUrl = product.dataset.url;
        const productTitle = product.dataset.title;
        const productImage = product.dataset.image;
        const productPrice = product.dataset.price;
        const productComparePrice = product.dataset.comparePrice;

        if (isFirstProduct) {
          // Update featured card with first product (image only)
          this.updateFeaturedCard(productUrl, productTitle, productImage);
          isFirstProduct = false;
        } else {
          // Add remaining products to carousel
          const card = this.createProductCard(productUrl, productTitle, productImage, productPrice, productComparePrice);
          this.carousel.appendChild(card);
        }

        count++;
      });

      this.updateNavButtons();

    } catch (error) {
      console.error('Error fetching products:', error);
      this.loadingEl.classList.remove('active');
      this.emptyEl.classList.add('active');
      if (this.featuredCard) {
        this.featuredCard.style.display = 'none';
      }
    }
  }

  updateFeaturedCard(url, title, image) {
    if (!this.featuredCard) return;

    this.featuredCard.href = url;
    this.featuredCard.style.display = 'block';

    const imgElement = this.featuredCard.querySelector('.shop-by-color__card-img');
    if (imgElement) {
      imgElement.src = image;
      imgElement.alt = title;
    }
  }

  createProductCard(url, title, image, price, comparePrice) {
    const card = document.createElement('a');
    card.href = url;
    card.className = 'shop-by-color__card';

    // Build price HTML
    let priceHtml = '';
    if (comparePrice && comparePrice !== price) {
      const comparePriceNum = parseFloat(comparePrice.replace(/[^0-9.-]+/g, ""));
      const priceNum = parseFloat(price.replace(/[^0-9.-]+/g, ""));
      if (comparePriceNum > priceNum) {
        priceHtml = `<span class="compare-price">${comparePrice}</span><span class="sale-price">${price}</span>`;
      } else {
        priceHtml = `<span class="sale-price">${price}</span>`;
      }
    } else {
      priceHtml = `<span class="sale-price">${price}</span>`;
    }

    card.innerHTML = `
      <div class="shop-by-color__card-image">
        <img class="shop-by-color__card-img" src="${image}" alt="${title}" loading="lazy">
      </div>
      <div class="shop-by-color__card-details">
        <h3 class="shop-by-color__card-title">${title}</h3>
        <div class="shop-by-color__card-price">${priceHtml}</div>
      </div>
    `;

    return card;
  }

  loadInitialProducts() {
    if (this.swatches.length > 0) {
      const firstSwatch = this.swatches[0];
      const colorValue = firstSwatch.dataset.colorValue;
      const paramName = firstSwatch.dataset.paramName;
      this.fetchFilteredProducts(paramName, colorValue);
    }
  }
}

// Initialize all shop-by-color sections on the page
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.shop-by-color');
  sections.forEach(section => {
    new ShopByColor(section);
  });
});

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.shop-by-color');
    if (section) {
      new ShopByColor(section);
    }
  });
}
